import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  SiteAgentArtSlot,
  SiteAgentBrandSuggestion,
  SiteAgentRunView,
  SuggestBrandInput,
} from '@icetus/contracts';
import { LOCALE_LABEL, LOCALES, TAXONOMY, findIndustry } from '@icetus/contracts';
import type { Locale } from '@icetus/contracts';
import {
  ApiError,
  fetchActiveSiteAgentRun,
  fetchSiteAgentRun,
  retrySiteAgentArt,
  startSiteAgentRun,
  suggestSiteAgentBrand,
} from '../api';

/**
 * AI 建站面板。
 *
 * ## 长得像对话框，但只有四个填位
 *
 * 用户看到的是"一句话 + 四个高亮格子"，看起来是在跟 AI 说话，实际每一格
 * 都是受控控件（三个输入 + 一个两级选择器），改不出第五种东西。
 * 抄的是外观，不是"自由对话" —— 自由对话的失败模式是模型在某一步停下，
 * 而用户看不出停在哪。
 *
 * ## 点「生成网站」是**校验**，不是提交
 *
 * 四格全可空。第一次点击只做三件事：缺什么就在同一个位置问一句、给可点的
 * 建议、齐了发一句确认。**确认之后要再点一次才真的开建** —— 建站会花掉
 * 十几张图，让用户看清要拿到什么再表态，比"点了就跑"更接近真实心理。
 * 代价是同一个按钮按两次，所以确认条里必须明写"建站要再点一次"。
 *
 * ## 追问链是纯前端状态
 *
 * 不落库、不占 run。四格的值暂存在 `localStorage`（刷新回得来），
 * 但确认条**不暂存** —— 它代表"我检查过了"，值一变就不该还算数。
 */

type Phase = 'form' | 'progress' | 'done';

interface Bubble {
  id: number;
  role: 'ai' | 'user';
  node: ReactNode;
}

/** 四格暂存的键。带 tenantId，换账号不会读到别人的输入 */
function draftKey(tenantId: string): string {
  return `icetus.site-agent.${tenantId}`;
}

interface Draft {
  company: string;
  brand: string;
  industry: string;
  segment: string;
  products: string;
  locale: Locale;
}

const EMPTY_DRAFT: Draft = {
  company: '',
  brand: '',
  industry: '',
  segment: '',
  products: '',
  locale: 'en',
};

export function SiteAgentPanel({
  tenantId,
  onDone,
  onCancel,
  onNavigate,
}: {
  tenantId: string;
  /** 建成之后通知外层重拉站点列表（侧栏也要从"只有我的站点"展开） */
  onDone: () => void;
  onCancel: () => void;
  onNavigate: (key: 'domains' | 'catalog') => void;
}): React.ReactElement {
  const [phase, setPhase] = useState<Phase>('form');
  const [draft, setDraft] = useState<Draft>(() => readDraft(tenantId));
  // 初始**不放气泡**：开口第一句由 hero 承担（吉祥物 + 一句话），
  // 气泡只在"追问"时出现。先摆一条说明"四格都可空"，等于把规则当寒暄讲出来，
  // 而用户真正会信的是"我点了没填，它来问我"。
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [suggestion, setSuggestion] = useState<SiteAgentBrandSuggestion | null>(null);
  const [rejected, setRejected] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<SiteAgentRunView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** 正在补的那一格（槽位名）。同一时间只补一张，也不允许连点 */
  const [retryingSlot, setRetryingSlot] = useState<string | null>(null);
  const [artError, setArtError] = useState<string | null>(null);

  const companyRef = useRef<HTMLInputElement>(null);
  const brandRef = useRef<HTMLInputElement>(null);
  const productsRef = useRef<HTMLInputElement>(null);
  const bubbleId = useRef(1);
  // 滚的是气泡区自己，不是整页：`scrollIntoView` 会连带把外面那层页面滚一下，
  // 用户看到的是"我刚填的字跳走了"
  const threadRef = useRef<HTMLDivElement>(null);

  const push = useCallback((role: Bubble['role'], node: ReactNode) => {
    setBubbles((prev) => [...prev, { id: bubbleId.current++, role, node }]);
  }, []);

  /* ── 草稿暂存 ──────────────────────────────────────────────── */

  useEffect(() => {
    try {
      window.localStorage.setItem(draftKey(tenantId), JSON.stringify(draft));
    } catch {
      // 隐私模式下 localStorage 会抛：那就不暂存，刷新丢字总比白屏好
    }
  }, [draft, tenantId]);

  /* ── 回到未完成的进度页 ─────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;
    fetchActiveSiteAgentRun(tenantId)
      .then((active) => {
        if (cancelled || !active) return;
        setRunId(active.id);
        setRun(active);
        setPhase(active.status === 'done' ? 'done' : 'progress');
      })
      .catch(() => {
        // 拉不到就当没有在跑的：这一天客户还能正常从表单开始，
        // 不该因为一个"查历史"的请求失败就把他挡在门外
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /* ── 轮询 ──────────────────────────────────────────────────── */

  useEffect(() => {
    if (!runId || phase === 'done') return;
    let cancelled = false;
    let timer: number | null = null;

    const tick = async (): Promise<void> => {
      try {
        const next = await fetchSiteAgentRun(tenantId, runId);
        if (cancelled) return;
        setRun(next);
        if (next.status === 'done') {
          setPhase('done');
          // 站点已经落库了，通知外层刷新 —— 侧栏也要从"只有我的站点"展开
          onDone();
          return;
        }
        if (next.status === 'failed') {
          // 停在进度页显示原因，不把用户扔回表单：他填过的四个格子还在下面
          return;
        }
      } catch {
        // 网络抖动不该打断进度页：下一跳再试
      }
      if (!cancelled) timer = window.setTimeout(() => void tick(), 2_000);
    };

    timer = window.setTimeout(() => void tick(), 1_200);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [runId, phase, tenantId, onDone]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [bubbles]);

  /* ── 交互 ──────────────────────────────────────────────────── */

  const industry = findIndustry(draft.industry);
  const segment = industry?.segments.find((item) => item.id === draft.segment) ?? null;
  const productList = splitProducts(draft.products);

  const patch = (next: Partial<Draft>): void => {
    setDraft((prev) => ({ ...prev, ...next }));
    // 值一变，那句"都齐了"就不算数了 —— 它代表"我检查过了"
    setConfirmed(false);
  };

  const askBrand = async (rejects: string[]): Promise<void> => {
    setBusy('brand');
    try {
      const payload: SuggestBrandInput = {
        company: draft.company.trim(),
        industry: draft.industry || null,
        segment: draft.segment || null,
        products: productList,
        locale: draft.locale,
        rejected: rejects,
      };
      const result = await suggestSiteAgentBrand(tenantId, payload);
      if (result.suggestion) {
        setSuggestion(result.suggestion);
        push('ai', brandAsk(result.suggestion));
      } else if (result.fallback) {
        // 模型不可用时的退路：把那格变成输入框，不阻塞建站
        setSuggestion(null);
        patch({ brand: result.fallback });
        push(
          'ai',
          <>
            取名这会儿不好使，先用 <b>{result.fallback}</b> 顶着 —— 你可以在上面那格改成想要的，
            或者之后在「站点信息」里改。
          </>,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  /**
   * 品牌那一问的气泡：候选卡 + 三个出口。
   *
   * **给候选而不是给输入框**：用户说"我自己填"其实是压力 —— 大部分中国工厂
   * 老板手上没有现成的英文品牌名。所以这一问的直接答案是"我给你起了一个"，
   * 三个出口是：就用 / 换一个 / 我自己填（原地把下面那格聚焦，不弹窗）。
   */
  const brandAsk = (item: SiteAgentBrandSuggestion): ReactNode => (
    <>
      <p>品牌名没想好就先用这个，不满意可以换：</p>
      <BrandCard suggestion={item} />
      <div className="agent-acts">
        <button type="button" className="agent-act agent-act--on" onClick={() => onPickSuggestion(item.name)}>
          就用 {item.name}
        </button>
        <button
          type="button"
          className="agent-act"
          disabled={busy === 'brand'}
          onClick={() => void onRejectSuggestion(item.name)}
        >
          {busy === 'brand' ? '换一个…' : '换一个'}
        </button>
        <button
          type="button"
          className="agent-act"
          onClick={() => {
            setSuggestion(null);
            brandRef.current?.focus();
          }}
        >
          我自己填
        </button>
      </div>
    </>
  );

  /**
   * 点「生成网站」。
   *
   * 第一次是**校验**：逐个补齐、逐个追问；齐了发一句确认并停住。
   * 第二次（`confirmed` 为真）才真的调 `/runs`。
   */
  const onPrimary = async (): Promise<void> => {
    if (busy) return;
    setError(null);

    if (confirmed) {
      setBusy('start');
      try {
        const started = await startSiteAgentRun(tenantId, {
          company: draft.company.trim(),
          brand: draft.brand.trim() || null,
          industry: draft.industry || null,
          segment: draft.segment || null,
          products: productList,
          locale: draft.locale,
        });
        setRunId(started.runId);
        setPhase('progress');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(null);
      }
      return;
    }

    // 1) 公司名：唯一一个模型编不出来的字段
    if (!draft.company.trim()) {
      push('ai', <>先给我公司全称吧 —— 站名、页脚、公司信息都靠它。</>);
      companyRef.current?.focus();
      return;
    }
    // 2) 产品：不填的话 12 件商品只能靠猜
    if (productList.length === 0) {
      const hints = segment ? segment.seeds.slice(0, 5).map((seed) => seed.zh) : [];
      push(
        'ai',
        <>
          产品有哪些？给三五个词就行，中文英文都可以。
          {hints.length > 0 ? (
            <div className="agent-chips">
              {hints.map((hint) => (
                <button
                  key={hint}
                  type="button"
                  className="agent-chip"
                  onClick={() => {
                    const next = [...productList, hint];
                    patch({ products: next.join('、') });
                  }}
                >
                  {hint}
                </button>
              ))}
            </div>
          ) : null}
        </>,
      );
      productsRef.current?.focus();
      return;
    }
    // 3) 品牌名：不是必问 —— 没想好就直接给候选
    if (!draft.brand.trim()) {
      if (!suggestion) {
        await askBrand(rejected);
        return;
      }
      push('ai', brandAsk(suggestion));
      return;
    }

    // 4) 齐了：说清楚会拿到什么，然后**停住**等第二次点击
    setConfirmed(true);
    push(
      'ai',
      <span className="agent-ok">
        <i aria-hidden="true">✓</i>
        <span>
          都齐了。<b>3 个页面 · 十几张配图 · 12 件商品，约 3 分钟</b>。
          点下面的「生成网站」就开始。
        </span>
      </span>,
    );
  };

  const onPickSuggestion = (name: string): void => {
    setDraft((prev) => ({ ...prev, brand: name }));
    setSuggestion(null);
    setConfirmed(false);
    push('user', name);
    push('ai', <>好，就用 <b>{name}</b>。齐了，点下面的「生成网站」就开始。</>);
    setConfirmed(true);
  };

  const onRejectSuggestion = async (name: string): Promise<void> => {
    const next = [...rejected, name];
    setRejected(next);
    setSuggestion(null);
    push('user', '换一个');
    await askBrand(next);
  };

  /* ── 补一张没生成的图 ──────────────────────────────────────── */

  /**
   * 交付页也能点。
   *
   * 客户通常是等整页跑完才回头看哪一格空着（那时运行已经是 `done`），
   * 只在进行中给按钮，等于把这个功能藏在他最想用的时候。
   * 点完把阶段拨回 `progress`：轮询只在那个阶段跑，不拨回去，
   * 补好的图不会自己出现在页面上。
   */
  const onRetryArt = async (slot: string): Promise<void> => {
    if (!runId || retryingSlot) return;
    setRetryingSlot(slot);
    setArtError(null);
    try {
      const next = await retrySiteAgentArt(tenantId, runId, slot);
      setRun(next);
      setPhase('progress');
    } catch (err) {
      setArtError(err instanceof ApiError ? err.message : String(err));
      setRetryingSlot(null);
    }
  };

  /**
   * 那一格真的开始跑了才解锁。
   *
   * 请求一回来它只是 `pending`（排队），队列可能还有几张在前头；这时就清掉
   * 忙状态的话，格子会先变回"未生成"、过两秒才转圈，看起来像点了个寂寞。
   */
  useEffect(() => {
    if (!retryingSlot || !run) return;
    const slot = run.art.slots.find((item) => item.slot === retryingSlot);
    if (slot && slot.status !== 'pending') setRetryingSlot(null);
  }, [run, retryingSlot]);

  /* ── 渲染 ──────────────────────────────────────────────────── */

  if (phase === 'progress' || phase === 'done') {
    return (
      <AgentProgress
        run={run}
        done={phase === 'done'}
        retrying={retryingSlot}
        artError={artError}
        onRetryArt={(slot) => void onRetryArt(slot)}
        onOpenSite={(host) => window.open(`https://${host}`, '_blank', 'noreferrer')}
        onNavigate={onNavigate}
        onClose={onCancel}
        onRetry={() => {
          setRunId(null);
          setRun(null);
          setPhase('form');
          setConfirmed(false);
          setBubbles([{ id: bubbleId.current++, role: 'ai', node: <>再试一次。四格还在这儿，改完点生成。</> }]);
        }}
      />
    );
  }

  return (
    <div className="agent">
      <button type="button" className="wiz__back" onClick={onCancel}>
        ← 我的站点
      </button>

      <div className="agent-card">
        {/*
         * 顶部照抄的是"一个面板"而不是"一个头部"：两个页签 + 右上角关闭，
         * 页签带下划线。少了这条线，整个卡片就只是一个白框，
         * 用户不知道自己进的是"AI 建站"还是"某个设置页"。
         */}
        <div className="agent-tabs">
          <span className="agent-tab is-on">AI 建站</span>
          <button type="button" className="agent-tab" onClick={onCancel}>
            选择模板
          </button>
          <button type="button" className="agent-x" onClick={onCancel} aria-label="返回我的站点">
            ×
          </button>
        </div>

        <div className={`agent-body${bubbles.length > 0 ? ' is-talk' : ''}`}>
          {bubbles.length === 0 ? (
            <div className="agent-hero">
              <AgentMascot />
              <h2>Hi～ 告诉我四件事，剩下的我来</h2>
            </div>
          ) : (
            <div className="agent-thread" ref={threadRef}>
              {bubbles.map((bubble) => (
                <div key={bubble.id} className={`agent-msg agent-msg--${bubble.role}`}>
                  {bubble.role === 'ai' ? <span className="agent-ava" aria-hidden="true" /> : null}
                  <div className="agent-bub">{bubble.node}</div>
                </div>
              ))}
            </div>
          )}

          <div className="agent-stack">
            <div className="agent-composer">
              <p className="agent-sentence">
                我的公司全称是{' '}
                <input
                  ref={companyRef}
                  className="agent-fx"
                  value={draft.company}
                  placeholder="在此输入公司名称"
                  maxLength={120}
                  onChange={(e) => patch({ company: e.target.value })}
                />
                ，品牌名称是{' '}
                <input
                  ref={brandRef}
                  className="agent-fx"
                  value={draft.brand}
                  placeholder="在此输入品牌名称"
                  maxLength={40}
                  onChange={(e) => patch({ brand: e.target.value })}
                />
                ，我属于{' '}
                <button
                  type="button"
                  className={`agent-fx agent-fx--btn${draft.industry ? '' : ' is-empty'}`}
                  onClick={() => setPickerOpen((open) => !open)}
                >
                  {industry ? `${industry.label}${segment ? ` · ${segment.label}` : ''}` : '选择所属行业'}
                  <i className="agent-caret" aria-hidden="true" />
                </button>{' '}
                {/* 中文可以逐字断行，"行"留在上一行、"业"掉到下一行比整句换行难看得多 */}
                <span className="agent-nowrap">行业，</span>
                我的产品有{' '}
                <input
                  ref={productsRef}
                  className="agent-fx agent-fx--wide"
                  value={draft.products}
                  placeholder="在此输入产品"
                  maxLength={200}
                  onChange={(e) => patch({ products: e.target.value })}
                />
                ，帮我生成一个外贸官网～
              </p>

              {pickerOpen ? (
                <div className="agent-picker">
                  <div className="agent-picker__col">
                    {TAXONOMY.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`agent-picker__l1${item.id === draft.industry ? ' is-on' : ''}`}
                        onClick={() => patch({ industry: item.id, segment: '' })}
                      >
                        {item.label}
                        <em>{item.segments.length}</em>
                      </button>
                    ))}
                  </div>
                  <div className="agent-picker__col agent-picker__col--seg">
                    {industry ? (
                      industry.segments.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`agent-picker__l2${item.id === draft.segment ? ' is-on' : ''}`}
                          onClick={() => {
                            patch({ industry: industry.id, segment: item.id });
                            setPickerOpen(false);
                          }}
                        >
                          <span>{item.label}</span>
                          <em>{item.seeds.slice(0, 3).map((seed) => seed.zh).join(' · ')}</em>
                        </button>
                      ))
                    ) : (
                      <p className="agent-picker__hint">先选左边的一级行业</p>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="agent-bar">
                <label className="agent-lang">
                  <span aria-hidden="true">🌐</span>
                  <span>网站语言</span>
                  <select
                    className="agent-select"
                    value={draft.locale}
                    onChange={(e) => patch({ locale: e.target.value as Locale })}
                  >
                    {LOCALES.map((code) => (
                      <option key={code} value={code}>
                        {LOCALE_LABEL[code]}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="agent-kbd">Ctrl + Enter 生成</span>
              </div>
            </div>

            {error ? <div className="notice notice--error">{error}</div> : null}

            <div className="agent-go">
              <button
                type="button"
                className="agent-go__btn"
                disabled={busy !== null}
                onClick={() => void onPrimary()}
              >
                {busy === 'start' ? '正在发起…' : busy === 'brand' ? '正在起名…' : '生成网站'}
              </button>
              {confirmed ? (
                <p className="agent-go__note">
                  刚才那一下只做了检查，<b>建站要再点一次</b>
                </p>
              ) : (
                <button type="button" className="agent-skip" onClick={onCancel}>
                  跳过 AI，直接选择精美模板 ›
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 开口第一屏的吉祥物。
 *
 * 用 `currentColor` / 令牌而不是写死 hex：换主题时它得跟着变，
 * 而且控制台的 `no-raw-color` 规则也不允许在组件里出现色值。
 */
function AgentMascot(): React.ReactElement {
  return (
    <svg className="agent-mascot" width="76" height="66" viewBox="0 0 76 66" aria-hidden="true">
      <rect x="14" y="12" width="48" height="40" rx="15" fill="var(--text)" />
      <rect x="26" y="27" width="6" height="12" rx="3" fill="var(--panel)" />
      <rect x="44" y="27" width="6" height="12" rx="3" fill="var(--panel)" />
      <circle cx="21" cy="57" r="2.5" fill="var(--brand)" />
      <circle cx="57" cy="58" r="2" fill="var(--warn)" />
      <path
        d="M64 8 l1.8 4.6 4.6 1.8 -4.6 1.8 -1.8 4.6 -1.8 -4.6 -4.6 -1.8 4.6 -1.8z"
        fill="var(--brand)"
        opacity="0.55"
      />
      <path
        d="M9 6 l1.4 3.6 3.6 1.4 -3.6 1.4 -1.4 3.6 -1.4 -3.6 -3.6 -1.4 3.6 -1.4z"
        fill="var(--brand)"
        opacity="0.3"
      />
    </svg>
  );
}

function BrandCard({ suggestion }: { suggestion: SiteAgentBrandSuggestion }): React.ReactElement {
  return (
    <div className="agent-name">
      <div className="agent-name__top">
        <b>{suggestion.name}</b>
        {suggestion.pronounce ? <span>{suggestion.pronounce}</span> : null}
      </div>
      {suggestion.reason ? <p className="agent-name__why">{suggestion.reason}</p> : null}
      <p className="agent-name__chk">
        已检查：{suggestion.checks.join('；')}。可注册性未核验，对外推广前建议先查商标。
      </p>
    </div>
  );
}

/**
 * 进度页与交付页。
 *
 * 两件事必须做到，否则这两三分钟会让人觉得"卡住了"：
 * **一、阶段要真的分得开。** 只有一个笼统的百分比，用户看不出是卡在建站
 * 还是卡在出图；这里按 `slots` 的 `kind` 把"品牌标识 / 站内配图 / 商品图"
 * 拆成三段，各自带 `3/12` 这样的计数。
 * **二、图一出就占格。** 空槽位也要画出来，让用户看见"还有 9 张在排队"，
 * 而不是一个空墙慢慢填。
 */
function AgentProgress({
  run,
  done,
  retrying,
  artError,
  onRetryArt,
  onOpenSite,
  onNavigate,
  onClose,
  onRetry,
}: {
  run: SiteAgentRunView | null;
  done: boolean;
  /** 正在补的那一格，`null` 表示没有在补 */
  retrying: string | null;
  artError: string | null;
  onRetryArt: (slot: string) => void;
  onOpenSite: (host: string) => void;
  onNavigate: (key: 'domains' | 'catalog') => void;
  onClose: () => void;
  onRetry: () => void;
}): React.ReactElement {
  const failed = run?.status === 'failed';
  const percent = Math.max(3, run?.percent ?? 3);

  // 算"已用多久"要有自己的心跳：轮询是每 2 秒一次，但内容没变时不会重渲染，
  // 时间就会僵在那儿，看起来像挂了
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (done || failed) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [done, failed]);

  const slots = run?.art.slots ?? [];
  const settled = (slot: (typeof slots)[number]): boolean =>
    slot.status === 'succeeded' || slot.status === 'failed';

  const logo = slots.filter((slot) => slot.kind === 'logo');
  const scene = slots.filter((slot) => slot.kind === 'block' || slot.kind === 'block-item');
  const products = slots.filter((slot) => slot.kind === 'product');
  /** 交付页也要列出这些：运行一旦 done，进度页就翻过去了 */
  const failedSlots = slots.filter((slot) => slot.status === 'failed');
  /**
   * 商品墙之外没生成的（Logo、首屏、图文分栏）。已经点过补图、还在排队的那格
   * 也留在这里转圈 —— 一按就从列表里消失，看起来像"补好了"。
   */
  const otherFailures = slots.filter(
    (slot) =>
      slot.kind !== 'product' &&
      (slot.status === 'failed' || (slot.status === 'pending' && retrying === slot.slot)),
  );

  const sceneDone = scene.filter(settled).length;
  const productDone = products.filter(settled).length;
  const elapsed = run ? Math.max(0, Math.round((now - new Date(run.createdAt).getTime()) / 1000)) : 0;

  const stages = [
    { key: 'site', label: '站点创建', hint: '', done: Boolean(run?.siteId) },
    {
      key: 'copy',
      label: '页面文案',
      hint: '',
      done: run?.status === 'art' || run?.status === 'done',
    },
    { key: 'logo', label: '品牌标识', hint: '', done: logo.length > 0 && logo.every(settled) },
    {
      key: 'scene',
      label: '站内配图',
      hint: scene.length > 0 ? `${sceneDone}/${scene.length}` : '',
      done: scene.length > 0 && sceneDone === scene.length,
    },
    {
      key: 'product',
      label: '商品图',
      hint: products.length > 0 ? `${productDone}/${products.length}` : '',
      done: products.length > 0 && productDone === products.length,
    },
    { key: 'live', label: '发布收尾', hint: '', done: run?.status === 'done' },
  ];
  const currentIdx = stages.findIndex((stage) => !stage.done);

  /* ── 顶部那两句人话 ─────────────────────────────────────────── */

  const running = slots.find((slot) => slot.status === 'running');
  let headline: string;
  if (failed) {
    headline = '生成中断了';
  } else if (done) {
    headline = '建好了';
  } else if (!run || run.status === 'queued') {
    headline = '正在排队，马上开始';
  } else if (run.status === 'building') {
    headline = '正在创建站点、写页面文案';
  } else if (running?.kind === 'product') {
    headline = `正在生成商品图 · 第 ${Math.min(productDone + 1, products.length)} 件，共 ${products.length} 件`;
  } else if (running?.kind === 'logo') {
    headline = '正在设计品牌标识';
  } else if (scene.length > 0) {
    headline = `正在生成站内配图 · 第 ${Math.min(sceneDone + 1, scene.length)} 张，共 ${scene.length} 张`;
  } else {
    headline = '正在生成配图';
  }

  // 预计剩余只按"图的产出速度"倒推。没出过图时不给估计 ——
  // 凭空给一个"约 3 分钟"，到点了没做完比不给更伤
  let eta = '';
  if (!done && !failed && run && run.art.done > 0 && run.art.total > run.art.done) {
    const per = elapsed / run.art.done;
    eta = `，预计还需 ${formatSpan(Math.round(per * (run.art.total - run.art.done)))}`;
  }
  const subline = done
    ? `用时 ${formatSpan(elapsed)}`
    : failed
      ? (run?.error?.message ?? '出错了，可以重新生成')
      : `已用 ${formatSpan(elapsed)}${eta}`;

  /* ── 交付态 ─────────────────────────────────────────────────── */

  if (done && run) {
    const heroShot = scene.find((slot) => slot.url) ?? logo.find((slot) => slot.url);
    const exampleCount = run.notes.find((note) => note.includes('AI 补'));

    return (
      <div className="agent">
        <button type="button" className="wiz__back" onClick={onClose}>
          ← 我的站点
        </button>

        <div className="agent-card">
          <div className="agent-tabs">
            <span className="agent-tab is-on">AI 建站</span>
            <span className="agent-tab is-mute">生成完成</span>
            <button type="button" className="agent-x" onClick={onClose} aria-label="返回我的站点">
              ×
            </button>
          </div>

          <div className="agent-body is-done">
            <div className="agent-okbar">
              <i aria-hidden="true">✓</i>
              <span>
                <b>网站已发布</b>，现在打开就能看到访客看到的样子。
              </span>
              {run.host ? (
                <button type="button" className="agent-okbar__link" onClick={() => onOpenSite(run.host!)}>
                  {run.host} →
                </button>
              ) : null}
            </div>

            <div className="agent-cols">
              <div className="agent-browser">
                <div className="agent-browser__chrome">
                  <i />
                  <i />
                  <i />
                  <span>{run.host ?? '你的站点'}</span>
                </div>
                {heroShot?.url ? (
                  <img className="agent-browser__shot" src={heroShot.url} alt="" />
                ) : (
                  <div className="agent-browser__ph" />
                )}
              </div>

              <div className="agent-stats">
                <p className="agent-stats__t">这次生成了什么</p>
                <div className="agent-stats__grid">
                  <div>
                    <b>3</b>
                    <span>已发布页面</span>
                  </div>
                  <div>
                    <b>{run.art.done}</b>
                    <span>张配图</span>
                  </div>
                  <div>
                    <b>2</b>
                    <span>个商品分类</span>
                  </div>
                  <div>
                    <b>12</b>
                    <span>件商品</span>
                  </div>
                </div>

                <p className="agent-flag">
                  {exampleCount ??
                    '价格不写具体数字，页面统一显示 Request a quote；标题、文案、图片都能到对应的页面里改。'}
                </p>

                {/*
                 * 交付页也要留一个补图入口。
                 * 运行是"所有图都结束就收工"，客户往往在这时才回头看哪一格空着 ——
                 * 只有进度页能点的话，他偏偏在最想补的时候点不到。
                 */}
                {failedSlots.length > 0 ? (
                  <div className="agent-miss">
                    <div className="agent-miss__head">
                      <span>还有 {failedSlots.length} 张配图没生成</span>
                      <span>点一下补上</span>
                    </div>
                    <div className="agent-miss__grid">
                      {failedSlots.map((slot) => (
                        <FailedTile
                          key={slot.slot}
                          slot={slot}
                          busy={retrying === slot.slot}
                          onRetry={onRetryArt}
                        />
                      ))}
                    </div>
                    {artError ? <p className="agent-miss__err">{artError}</p> : null}
                  </div>
                ) : null}

                <div className="agent-acts">
                  <button
                    type="button"
                    className="agent-go__btn agent-go__btn--sm"
                    onClick={() => run.host && onOpenSite(run.host)}
                  >
                    打开网站
                  </button>
                  <button type="button" className="agent-act" onClick={() => onNavigate('catalog')}>
                    去改商品
                  </button>
                </div>
                <button
                  type="button"
                  className="agent-skip agent-skip--left"
                  onClick={() => onNavigate('domains')}
                >
                  绑定自己的域名 ›
                </button>

                <p className="agent-stats__foot">
                  要调内容到「页面 / 商品 / 主题」里改，这里不提供重跑生成。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── 进行中 / 失败 ──────────────────────────────────────────── */

  return (
    <div className="agent">
      <button type="button" className="wiz__back" onClick={onClose}>
        ← 我的站点
      </button>

      <div className="agent-card">
        <div className="agent-tabs">
          <span className="agent-tab is-on">AI 建站</span>
          <span className="agent-tab is-mute">{failed ? '中断' : '生成中'}</span>
          <button type="button" className="agent-x" onClick={onClose} aria-label="返回我的站点">
            ×
          </button>
        </div>

        <div className="agent-body is-progress">
          <div className="agent-prog">
            <div className="agent-prog__top">
              <div>
                <h2>{headline}</h2>
                <p>{subline}</p>
              </div>
              <span className={`agent-prog__pct${failed ? ' is-fail' : ''}`}>
                {failed ? '—' : `${percent}%`}
              </span>
            </div>
            <div className="agent-bar-progress">
              {/* 失败时停在断点，不回退到 0 —— 客户要看到"走到哪一步断的" */}
              <i style={{ inlineSize: `${percent}%` }} />
            </div>
            <div className="agent-stages">
              {stages.map((stage, index) => {
                const state = stage.done
                  ? 'is-done'
                  : index === currentIdx
                    ? failed
                      ? 'is-stuck'
                      : 'is-now'
                    : '';
                return (
                  <span key={stage.key} className={`agent-stage ${state}`}>
                    <i aria-hidden="true" />
                    {stage.label}
                    {stage.hint ? <em>{stage.hint}</em> : null}
                  </span>
                );
              })}
            </div>
            <p className="agent-hint">
              <i aria-hidden="true" />
              {failed
                ? '站点本身已经建好了，缺的是配图。空着的那一格点一下可以再试一次，也可以自己传一张。'
                : '网站正在生成，请不要离开或关闭页面。关掉也不会丢，回来还能看到进度。'}
            </p>
          </div>

          {products.length > 0 ? (
            <div className="agent-cols">
              <div className="agent-panel-card">
                <div className="agent-panel-card__head">
                  <span>商品图</span>
                  <span>
                    {productDone} / {products.length}
                  </span>
                </div>
                <div className="agent-wall__grid">
                  {products.map((slot) => {
                    const busy = retrying === slot.slot;
                    if (slot.status === 'failed') {
                      return (
                        <FailedTile key={slot.slot} slot={slot} busy={busy} onRetry={onRetryArt} />
                      );
                    }
                    // 刚补上、还在排队的格子也转起来：空白格看不出"我点的那下生效了"
                    const waiting = busy && slot.status === 'pending';
                    return (
                      <div
                        key={slot.slot}
                        className={`agent-shot agent-shot--${waiting ? 'running' : slot.status}`}
                        title={slot.error ?? slot.slot}
                      >
                        {slot.url ? <img src={slot.url} alt="" loading="lazy" /> : null}
                        {slot.status === 'running' || waiting ? (
                          <span className="agent-spin" aria-hidden="true" />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="agent-panel-card">
                <div className="agent-panel-card__head">
                  <span>已完成</span>
                  <span>{stages.filter((stage) => stage.done).length} 项</span>
                </div>

                {stages
                  .filter((stage) => stage.done)
                  .map((stage) => (
                    <p key={stage.key} className="agent-tick">
                      <i aria-hidden="true">✓</i>
                      {stage.label}
                      {stage.hint ? <em>{stage.hint}</em> : null}
                    </p>
                  ))}

                {scene.some((slot) => slot.url) ? (
                  <div className="agent-mini">
                    {scene
                      .filter((slot) => slot.url)
                      .slice(0, 6)
                      .map((slot) => (
                        <i key={slot.slot}>
                          <img src={slot.url!} alt="" loading="lazy" />
                        </i>
                      ))}
                  </div>
                ) : null}

                {stages.some((stage) => !stage.done) ? (
                  <>
                    <p className="agent-grp">接下来</p>
                    {stages
                      .filter((stage) => !stage.done)
                      .map((stage) => (
                        <p key={stage.key} className="agent-tick is-todo">
                          <i aria-hidden="true">·</i>
                          {stage.label}
                          {stage.hint ? <em>{stage.hint}</em> : null}
                        </p>
                      ))}
                  </>
                ) : null}

                {/*
                 * 商品图之外没生成的那些（Logo、首屏、图文分栏）也在这里补。
                 * 它们的格子在页面别处，客户看不到"哪一格空着"，只有这一处能告诉他。
                 */}
                {otherFailures.length > 0 ? (
                  <>
                    <p className="agent-grp">没生成 · 点一下补</p>
                    <div className="agent-miss__grid">
                      {otherFailures.map((slot) => (
                        <FailedTile
                          key={slot.slot}
                          slot={slot}
                          busy={retrying === slot.slot}
                          onRetry={onRetryArt}
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {run && run.notes.length > 0 ? (
            <div className="notice notice--warn">{run.notes.join('；')}</div>
          ) : null}
          {artError ? <div className="notice notice--warn">{artError}</div> : null}

          {/* 失败时不给动画：它代表的"正在往前推进"已经不成立了 */}
          {failed ? null : <AgentBuilding />}

          <div className="agent-donebar">
            {failed ? (
              <>
                <button type="button" className="agent-go__btn agent-go__btn--sm" onClick={onRetry}>
                  重新生成
                </button>
                <button
                  type="button"
                  className="agent-act"
                  onClick={() => run?.host && onOpenSite(run.host)}
                >
                  先看看建好的站
                </button>
              </>
            ) : (
              <button type="button" className="agent-skip" onClick={onClose}>
                先回去，它会在后台继续 ›
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 一格没生成的图。**它是按钮，不是灰块。**
 *
 * 出图失败基本都是偶发（上游超时、一次没画完），重试一次通常就好。
 * 之前这里只写"未生成"，客户能做的是自己去媒体库找位置、自己拍一张 ——
 * 而我们知道该补哪儿、也知道该画什么，没有理由把这件事推回去。
 *
 * `title` 里带原始错误：客户不一定看得懂，但排查时能一眼看到是超时还是被挡了。
 */
function FailedTile({
  slot,
  busy,
  onRetry,
}: {
  slot: SiteAgentArtSlot;
  busy: boolean;
  onRetry: (slot: string) => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className="agent-shot agent-shot--failed agent-shot--retry"
      onClick={() => onRetry(slot.slot)}
      disabled={busy}
      title={slot.error ? `${slot.error}（点一下再试一次）` : '点一下再试一次'}
      aria-label="重新生成这张配图"
    >
      {busy ? (
        <span className="agent-spin" aria-hidden="true" />
      ) : (
        <>
          <span className="agent-shot__cta">
            <i aria-hidden="true">↻</i>
            补一张
          </span>
          <span className="agent-shot__sub">未生成</span>
        </>
      )}
    </button>
  );
}

/**
 * 生成中底部的小动画。
 *
 * 出图那两分钟里，卡片下半屏原本是一片空白 —— 客户分不清"在做"和"卡死"。
 * 这里放一个**不撒谎**的动画：小机器人把砖一块块码上去，说明正在搭页面。
 * 不用转圈 —— 转圈只传达"卡住了"，而这个进程其实在稳定前进。
 *
 * 纯装饰，`aria-hidden`：读屏不念，进度信息仍然由上面的阶段与百分比承担。
 * 颜色一律走令牌（`no-raw-color` 也不允许写死色值），换主题时它跟着变。
 */
function AgentBuilding(): React.ReactElement {
  return (
    <div className="agent-fun" aria-hidden="true">
      <svg className="agent-fun__art" width="220" height="118" viewBox="0 0 220 118">
        {/* 地面与影子：没有这两笔，小机器人会像漂在空中 */}
        <rect x="26" y="103" width="168" height="2" rx="1" fill="var(--line)" />
        <ellipse cx="72" cy="103" rx="27" ry="4" fill="var(--panel-2)" />

        {/* 三块砖从下往上码，越往上越窄 —— 一个"塔"比三根等宽的条更像在建东西 */}
        <rect className="agent-fun__brick" x="136" y="89" width="52" height="13" rx="4" fill="var(--brand)" opacity="0.85" />
        <rect className="agent-fun__brick agent-fun__brick--2" x="141" y="75" width="42" height="13" rx="4" fill="var(--brand)" opacity="0.55" />
        <rect className="agent-fun__brick agent-fun__brick--3" x="146" y="61" width="32" height="13" rx="4" fill="var(--brand)" opacity="0.32" />

        {/* 小机器人：圆角方块 + 两只眼睛，和开口屏那个吉祥物是同一只。
            底边压在 102（地面上沿），bob 只往上浮，不会陷进地里 */}
        <g className="agent-fun__bot">
          <rect x="50" y="62" width="44" height="40" rx="14" fill="var(--text)" />
          <rect className="agent-fun__eye" x="61" y="75" width="5" height="11" rx="2.5" fill="var(--panel)" />
          <rect className="agent-fun__eye" x="77" y="75" width="5" height="11" rx="2.5" fill="var(--panel)" />
          <circle cx="72" cy="57" r="3" fill="var(--brand)" />
        </g>

        {/* 三颗一闪一闪的小星星，给静态画面一点呼吸 */}
        <path
          className="agent-fun__spark"
          d="M112 40 l1.9 4.9 4.9 1.9 -4.9 1.9 -1.9 4.9 -1.9 -4.9 -4.9 -1.9 4.9 -1.9z"
          fill="var(--brand)"
        />
        <path
          className="agent-fun__spark agent-fun__spark--2"
          d="M34 34 l1.4 3.6 3.6 1.4 -3.6 1.4 -1.4 3.6 -1.4 -3.6 -3.6 -1.4 3.6 -1.4z"
          fill="var(--warn)"
        />
        <path
          className="agent-fun__spark agent-fun__spark--3"
          d="M196 40 l1.2 3.2 3.2 1.2 -3.2 1.2 -1.2 3.2 -1.2 -3.2 -3.2 -1.2 3.2 -1.2z"
          fill="var(--brand)"
        />
      </svg>
      <p className="agent-fun__cap">小机器人正在一块块搭你的网站</p>
    </div>
  );
}

/** 把秒数说成人话。超过一分钟给"1 分 48 秒"，避免"108 秒"这种要心算的写法 */
function formatSpan(seconds: number): string {
  if (seconds < 60) return `${seconds} 秒`;
  const min = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${min} 分 ${rest} 秒`;
}

function splitProducts(raw: string): string[] {
  return raw
    .split(/[、,，;；\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 12);
}

function readDraft(tenantId: string): Draft {
  try {
    const raw = window.localStorage.getItem(draftKey(tenantId));
    if (!raw) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw) as Partial<Draft>;
    return {
      company: typeof parsed.company === 'string' ? parsed.company : '',
      brand: typeof parsed.brand === 'string' ? parsed.brand : '',
      industry: typeof parsed.industry === 'string' ? parsed.industry : '',
      segment: typeof parsed.segment === 'string' ? parsed.segment : '',
      products: typeof parsed.products === 'string' ? parsed.products : '',
      locale: LOCALES.includes(parsed.locale as Locale) ? (parsed.locale as Locale) : 'en',
    };
  } catch {
    return EMPTY_DRAFT;
  }
}
